import { useState, useEffect } from 'react';
import storeProductsService from '../../services/storeproducts.service';
import { UNIT_MEASURE_OPTIONS } from '../../constants/unitMeasures';

const UNIT_OPTIONS = [
  { value: 'box', label: 'Caja' },
  { value: 'case', label: 'Caja grande' },
  { value: 'bag', label: 'Bolsa' },
  { value: 'pallet', label: 'Tarima' },
  { value: 'each', label: 'Pieza' },
  { value: 'lb', label: 'Libra' },
  { value: 'kg', label: 'Kilo' },
  { value: 'gallon', label: 'Galon' },
  { value: 'liter', label: 'Litro' },
  { value: 'dozen', label: 'Docena' },
];

const getUnitLabel = (value) => {
  const unit = UNIT_OPTIONS.find(u => u.value === value);
  return unit ? unit.label : value;
};

export default function HQProductManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Product form
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    orderUnit: 'box',
    pricingUnit: 'box',
    pricePerPricingUnit: '',
    estimatedConversion: '',
    unitMeasure: '',
  });
  const [addingProduct, setAddingProduct] = useState(false);

  // Edit product
  const [editingProductId, setEditingProductId] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    const unsubscribe = storeProductsService.subscribeProducts((data) => {
      setProducts(data);
      setLoading(false);
    });

    return () => unsubscribe && unsubscribe();
  }, []);

  const handleAddProduct = async (e) => {
    e.preventDefault();
    if (!newProduct.name.trim() || !newProduct.pricePerPricingUnit) return;

    setAddingProduct(true);
    try {
      const hasDualUnits = newProduct.orderUnit !== newProduct.pricingUnit;
      const estimatedConversion = hasDualUnits && newProduct.estimatedConversion
        ? parseFloat(newProduct.estimatedConversion)
        : null;

      await storeProductsService.addProduct({
        name: newProduct.name.trim(),
        orderUnit: newProduct.orderUnit,
        pricingUnit: newProduct.pricingUnit,
        pricePerPricingUnit: parseFloat(newProduct.pricePerPricingUnit),
        estimatedConversion,
        unitMeasure: newProduct.unitMeasure || null,
      });

      setNewProduct({
        name: '',
        orderUnit: 'box',
        pricingUnit: 'box',
        pricePerPricingUnit: '',
        estimatedConversion: '',
        unitMeasure: '',
      });
      setShowAddProduct(false);
    } catch (error) {
      console.error('Error adding product:', error);
      alert('Error al agregar producto. Intenta de nuevo.');
    } finally {
      setAddingProduct(false);
    }
  };

  const handleStartEdit = (product) => {
    setEditingProductId(product.id);
    setEditProduct({
      name: product.name,
      orderUnit: product.orderUnit,
      pricingUnit: product.pricingUnit,
      pricePerPricingUnit: product.pricePerPricingUnit,
      estimatedConversion: product.estimatedConversion || '',
      unitMeasure: product.unitMeasure || '',
    });
    setShowAddProduct(false);
  };

  const handleCancelEdit = () => {
    setEditingProductId(null);
    setEditProduct(null);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editProduct.name.trim() || !editProduct.pricePerPricingUnit) return;

    setSavingEdit(true);
    try {
      const hasDualUnits = editProduct.orderUnit !== editProduct.pricingUnit;
      const estimatedConversion = hasDualUnits && editProduct.estimatedConversion
        ? parseFloat(editProduct.estimatedConversion)
        : null;

      await storeProductsService.updateProduct(editingProductId, {
        name: editProduct.name.trim(),
        orderUnit: editProduct.orderUnit,
        pricingUnit: editProduct.pricingUnit,
        pricePerPricingUnit: parseFloat(editProduct.pricePerPricingUnit),
        estimatedConversion,
        unitMeasure: editProduct.unitMeasure || null,
      });

      setEditingProductId(null);
      setEditProduct(null);
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Error al actualizar producto. Intenta de nuevo.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeactivateProduct = async (productId, productName) => {
    if (!window.confirm(`Eliminar "${productName}" del catalogo?`)) return;

    try {
      await storeProductsService.deactivateProduct(productId);
    } catch (error) {
      console.error('Error deactivating product:', error);
      alert('Error al eliminar producto. Intenta de nuevo.');
    }
  };

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="merchant-panel">
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="merchant-panel">
      <div className="merchant-panel-header">
        <h2>Catalogo de Productos</h2>
      </div>

      <div style={{ padding: '0 1rem', marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="Buscar productos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem',
            fontSize: '1rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxSizing: 'border-box'
          }}
        />
      </div>

      <div className="merchant-form-section">
        <div className="section-header-row">
          <h3>Productos</h3>
          {!showAddProduct && (
            <button
              type="button"
              className="action-button secondary"
              onClick={() => setShowAddProduct(true)}
            >
              + Agregar Producto
            </button>
          )}
        </div>

        {showAddProduct && (
          <div className="inline-add-form" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={newProduct.name}
                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                placeholder="Nombre del producto (ej. Diesmillo)"
                autoFocus
                style={{ flex: 2, minWidth: '150px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <div className="formEntry" style={{ flex: 1, minWidth: '100px' }}>
                <label>Ordenar en</label>
                <select
                  value={newProduct.orderUnit}
                  onChange={(e) => setNewProduct({ ...newProduct, orderUnit: e.target.value })}
                >
                  {UNIT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="formEntry" style={{ flex: 1, minWidth: '100px' }}>
                <label>Precio por</label>
                <select
                  value={newProduct.pricingUnit}
                  onChange={(e) => setNewProduct({ ...newProduct, pricingUnit: e.target.value })}
                >
                  {UNIT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="formEntry" style={{ flex: 1, minWidth: '100px' }}>
                <label>Precio por {getUnitLabel(newProduct.pricingUnit)}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newProduct.pricePerPricingUnit}
                  onChange={(e) => setNewProduct({ ...newProduct, pricePerPricingUnit: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              {newProduct.orderUnit !== newProduct.pricingUnit && (
                <div className="formEntry" style={{ flex: 1, minWidth: '120px' }}>
                  <label>{getUnitLabel(newProduct.pricingUnit)}s por {getUnitLabel(newProduct.orderUnit)} (est.)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={newProduct.estimatedConversion}
                    onChange={(e) => setNewProduct({ ...newProduct, estimatedConversion: e.target.value })}
                    placeholder="ej. 40"
                  />
                </div>
              )}
              <div className="formEntry" style={{ flex: 1, minWidth: '120px' }}>
                <label>Medida</label>
                <select
                  value={newProduct.unitMeasure}
                  onChange={(e) => setNewProduct({ ...newProduct, unitMeasure: e.target.value })}
                >
                  {UNIT_MEASURE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="action-button secondary"
                onClick={() => {
                  setShowAddProduct(false);
                  setNewProduct({
                    name: '',
                    orderUnit: 'box',
                    pricingUnit: 'box',
                    pricePerPricingUnit: '',
                    estimatedConversion: '',
                    unitMeasure: '',
                  });
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleAddProduct}
                disabled={addingProduct || !newProduct.name.trim() || !newProduct.pricePerPricingUnit}
              >
                {addingProduct ? 'Agregando...' : 'Agregar Producto'}
              </button>
            </div>
          </div>
        )}

        {products.length === 0 ? (
          <p style={{ color: '#6c6c7b', padding: '1rem' }}>
            No hay productos. Agrega tu primer producto arriba.
          </p>
        ) : filteredProducts.length === 0 ? (
          <p style={{ color: '#6c6c7b', padding: '1rem' }}>
            No se encontraron productos. Intenta otra busqueda.
          </p>
        ) : (
          <div className="product-list">
            {filteredProducts.map(product => {
              if (editingProductId === product.id) {
                return (
                  <div key={product.id} className="inline-add-form" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <input
                        type="text"
                        value={editProduct.name}
                        onChange={(e) => setEditProduct({ ...editProduct, name: e.target.value })}
                        placeholder="Nombre del producto"
                        autoFocus
                        style={{ flex: 2, minWidth: '150px' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <div className="formEntry" style={{ flex: 1, minWidth: '100px' }}>
                        <label>Ordenar en</label>
                        <select
                          value={editProduct.orderUnit}
                          onChange={(e) => setEditProduct({ ...editProduct, orderUnit: e.target.value })}
                        >
                          {UNIT_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="formEntry" style={{ flex: 1, minWidth: '100px' }}>
                        <label>Precio por</label>
                        <select
                          value={editProduct.pricingUnit}
                          onChange={(e) => setEditProduct({ ...editProduct, pricingUnit: e.target.value })}
                        >
                          {UNIT_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="formEntry" style={{ flex: 1, minWidth: '100px' }}>
                        <label>Precio por {getUnitLabel(editProduct.pricingUnit)}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editProduct.pricePerPricingUnit}
                          onChange={(e) => setEditProduct({ ...editProduct, pricePerPricingUnit: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                      {editProduct.orderUnit !== editProduct.pricingUnit && (
                        <div className="formEntry" style={{ flex: 1, minWidth: '120px' }}>
                          <label>{getUnitLabel(editProduct.pricingUnit)}s por {getUnitLabel(editProduct.orderUnit)}</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={editProduct.estimatedConversion}
                            onChange={(e) => setEditProduct({ ...editProduct, estimatedConversion: e.target.value })}
                            placeholder="ej. 40"
                          />
                        </div>
                      )}
                      <div className="formEntry" style={{ flex: 1, minWidth: '120px' }}>
                        <label>Medida</label>
                        <select
                          value={editProduct.unitMeasure}
                          onChange={(e) => setEditProduct({ ...editProduct, unitMeasure: e.target.value })}
                        >
                          {UNIT_MEASURE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="action-button secondary"
                        onClick={handleCancelEdit}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveEdit}
                        disabled={savingEdit || !editProduct.name.trim() || !editProduct.pricePerPricingUnit}
                      >
                        {savingEdit ? 'Guardando...' : 'Guardar'}
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div key={product.id} className="product-list-item">
                  <div className="product-info">
                    <span className="product-name">{product.name}</span>
                    <span className="product-details">
                      {product.hasDualUnits() ? (
                        <>
                          Orden: {product.getUnitDisplay()} | {product.getPriceDisplay()}
                          {product.estimatedConversion && (
                            <> (~{product.estimatedConversion} {getUnitLabel(product.pricingUnit)}/{getUnitLabel(product.orderUnit)})</>
                          )}
                        </>
                      ) : (
                        <>{product.getUnitDisplay()} | {product.getPriceDisplay()}</>
                      )}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      className="action-button secondary"
                      onClick={() => handleStartEdit(product)}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      className="action-button danger"
                      onClick={() => handleDeactivateProduct(product.id, product.name)}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
